import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { BookOpen, Heart, Users, Bell, Music, Sparkles, ArrowRight, Shield } from 'lucide-react';

const LandingPage = () => {
    const features = [
        {
            icon: <Heart className="w-6 h-6" />,
            title: 'Mood Tracking',
            description: 'Log your emotions with ratings, emojis, and music that matches your mood'
        },
        {
            icon: <Sparkles className="w-6 h-6" />,
            title: 'Voice Journaling',
            description: 'Record voice entries to capture your thoughts and feelings'
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: 'Accountability Partners',
            description: 'Share your journey with trusted friends who can support you'
        },
        {
            icon: <Bell className="w-6 h-6" />,
            title: 'Smart Reminders',
            description: 'Set personalized reminders to maintain your journaling habit'
        },
        {
            icon: <Music className="w-6 h-6" />,
            title: 'YouTube Music',
            description: 'Play soothing music while you write to create the perfect atmosphere'
        },
        {
            icon: <BookOpen className="w-6 h-6" />,
            title: 'Personalized Prompts',
            description: 'Get AI prompts tailored to your recent entries and emotional state'
        }
    ];

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1603276730862-cbf79a742aae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbmF0dXJlJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc2NzU5MjI4OXww&ixlib=rb-4.1.0&q=85')`
                    }}
                />
                <div className="absolute inset-0 bg-black/50" />
                
                <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 sm:py-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center text-white"
                    >
                        <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-8">
                            <BookOpen className="w-10 h-10" />
                        </div>
                        
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6 leading-tight">
                            Your Sanctuary for<br />Emotional Wellness
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10">
                            A beautiful, private space to track your moods, express your feelings, 
                            and grow on your journey to mental, emotional, and spiritual healing.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/register">
                                <Button 
                                    data-testid="get-started-btn"
                                    className="rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 bg-white text-primary hover:bg-white/90"
                                >
                                    Start Your Journey
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button 
                                    variant="outline"
                                    data-testid="sign-in-btn"
                                    className="rounded-full px-8 py-6 text-lg font-medium border-white/30 text-white hover:bg-white/10"
                                >
                                    Sign In
                                </Button>
                            </Link>
                        </div>
                        
                        {/* Family Plan Banner */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="mt-8"
                        >
                            <Link to="/family/pricing" data-testid="family-plan-link">
                                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all">
                                    <Shield className="w-5 h-5 text-amber-400" />
                                    <span className="text-white/90">
                                        <strong>Family Plans</strong> - Keep your children safe with parental monitoring
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-white/70" />
                                </div>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Features Section */}
            <div className="max-w-6xl mx-auto px-4 py-20">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-4">
                        Everything You Need to Heal
                    </h2>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        MoodTrack combines journaling with powerful features designed to support your emotional wellbeing
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="glass-card rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                        >
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 text-primary">
                                {feature.icon}
                            </div>
                            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                                {feature.title}
                            </h3>
                            <p className="text-muted-foreground">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-primary/5 py-20">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-4">
                            Ready to Begin?
                        </h2>
                        <p className="text-muted-foreground text-lg mb-8">
                            Join thousands of others who are using MoodTrack to understand themselves better
                        </p>
                        <Link to="/register">
                            <Button 
                                data-testid="cta-btn"
                                className="rounded-full px-10 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                            >
                                Create Free Account
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 border-t border-border">
                <div className="max-w-6xl mx-auto px-4 text-center text-muted-foreground">
                    <p>&copy; 2024 MoodTrack. Your journey to wellness starts here.</p>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
